import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { SecurityPermission } from '@ic/security-registry';
import { canAccess } from '@/lib/access';
import { getConfig } from '@/lib/config';
import { resetAnalytics } from '@/lib/analytics';
import { setAuthAccessors, setBaseURL } from '@/api/client';
import { validateIdentity, getEffectivePermissions } from '@/api/auth';
import type {
  AuthState,
  EffectivePermissions,
  Site,
  UserProfile,
} from '@/types';

export interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setActiveSite: (siteId: string) => void;
  hasPermission: (permission: SecurityPermission) => boolean;
  /**
   * Re-reads `/api/auth/effective-permissions` into this provider's state. Used
   * after a security change (Security Controls Save / Inactivate / Activate) so
   * the menu and action-bar gating reflect it without a page reload (ADR-13).
   * Optional so the many partial `AuthContextValue` objects built by tests and
   * stories stay valid; both real providers implement it.
   */
  refreshPermissions?: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const REFRESH_AT_PERCENT = 0.8;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [effectivePermissions, setEffectivePermissions] = useState<EffectivePermissions | null>(null);
  const [activeSiteId, setActiveSiteIdState] = useState<string | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [lastLogin, setLastLogin] = useState<string | null>(null);

  const sessionRef = useRef<CognitoUserSession | null>(null);
  const cognitoUserRef = useRef<CognitoUser | null>(null);
  const userPoolRef = useRef<CognitoUserPool | null>(null);
  // Latest-wins sequencing for refreshPermissions: login, logout and every refresh take a new number,
  // and a refresh result is applied only if its number is still the latest. So an older overlapping
  // refresh, or one resolving after logout (or a new login), never overwrites newer state.
  const permissionsSeqRef = useRef(0);

  // Initialize Cognito user pool and attempt session restoration
  useEffect(() => {
    const config = getConfig();
    setBaseURL(config.apiBaseUrl);

    userPoolRef.current = new CognitoUserPool({
      UserPoolId: config.cognitoUserPoolId,
      ClientId: config.cognitoClientId,
    });

    // Wire up apiClient auth accessors (must happen before restoration)
    setAuthAccessors({
      getIdToken: () => sessionRef.current?.getIdToken().getJwtToken() ?? null,
      getActiveSiteId: () => activeSiteId,
      refreshSession: () => refreshSessionAsync(),
      onAuthFailure: () => logout(),
    });

    // Attempt to restore session from stored refresh token
    const existingUser = userPoolRef.current.getCurrentUser();
    if (existingUser) {
      cognitoUserRef.current = existingUser;
      existingUser.getSession(async (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          cognitoUserRef.current = null;
          setIsLoading(false);
          return;
        }

        sessionRef.current = session;

        try {
          const identityResult = await validateIdentity();
          setUser(identityResult.user);
          setRoles(identityResult.roles);
          setSites(identityResult.sites);
          setLastLogin(identityResult.lastLogin);

          if (identityResult.sites.length > 0) {
            setActiveSiteIdState(identityResult.sites[0]!.siteId);
          }

          const perms = await getEffectivePermissions();
          setEffectivePermissions(perms);

          setIsAuthenticated(true);
        } catch {
          existingUser.signOut();
          sessionRef.current = null;
          cognitoUserRef.current = null;
        } finally {
          setIsLoading(false);
        }
      });
    } else {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Proactive token refresh — schedule at 80% of token lifespan
  useEffect(() => {
    const session = sessionRef.current;
    const cognitoUser = cognitoUserRef.current;
    if (!session || !cognitoUser) return;

    const issuedAtMs = session.getIdToken().getIssuedAt() * 1000;
    const expiresAtMs = session.getIdToken().getExpiration() * 1000;
    const lifespanMs = expiresAtMs - issuedAtMs;
    const refreshAtMs = issuedAtMs + lifespanMs * REFRESH_AT_PERCENT;
    const delayMs = Math.max(refreshAtMs - Date.now(), 0);

    const timerId = setTimeout(() => {
      cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
        if (err) {
          console.warn('Token refresh failed — logging out');
          logout();
          return;
        }
        sessionRef.current = newSession;
      });
    }, delayMs);

    return () => clearTimeout(timerId);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshSessionAsync = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = cognitoUserRef.current;
      const session = sessionRef.current;
      if (!cognitoUser || !session) {
        reject(new Error('No active session'));
        return;
      }
      cognitoUser.refreshSession(session.getRefreshToken(), (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }
        sessionRef.current = newSession;
        resolve(newSession.getIdToken().getJwtToken());
      });
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const pool = userPoolRef.current;
    if (!pool) throw new Error('Cognito not initialized');

    const cognitoUser = new CognitoUser({
      Username: username,
      Pool: pool,
    });

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    // SRP authentication — password never sent in cleartext
    const session = await new Promise<CognitoUserSession>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (result) => resolve(result),
        onFailure: (err) => reject(err),
        newPasswordRequired: () => {
          reject(new Error('NEW_PASSWORD_REQUIRED'));
        },
      });
    });

    sessionRef.current = session;
    cognitoUserRef.current = cognitoUser;
    permissionsSeqRef.current += 1;

    // Validate identity against SQL Server Identity table
    const identityResult = await validateIdentity();
    setUser(identityResult.user);
    setRoles(identityResult.roles);
    setSites(identityResult.sites);
    setLastLogin(identityResult.lastLogin);

    if (identityResult.sites.length > 0) {
      setActiveSiteIdState(identityResult.sites[0]!.siteId);
    }

    // Load effective permissions
    const perms = await getEffectivePermissions();
    setEffectivePermissions(perms);

    setIsAuthenticated(true);
  }, []);

  const logout = useCallback(() => {
    // Synchronous because DevAuthProvider reloads the page immediately; the
    // AnalyticsIdentity effect would not flush first. Idempotent with the
    // effect's own reset (ADR-8).
    resetAnalytics();
    permissionsSeqRef.current += 1;
    const cognitoUser = cognitoUserRef.current;
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    sessionRef.current = null;
    cognitoUserRef.current = null;
    setIsAuthenticated(false);
    setUser(null);
    setRoles([]);
    setEffectivePermissions(null);
    setActiveSiteIdState(null);
    setSites([]);
    setLastLogin(null);
  }, []);

  const setActiveSite = useCallback((siteId: string) => {
    setActiveSiteIdState(siteId);
  }, []);

  const hasPermission = useCallback(
    (permission: SecurityPermission): boolean => canAccess(effectivePermissions, permission),
    [effectivePermissions],
  );

  const refreshPermissions = useCallback(async (): Promise<void> => {
    permissionsSeqRef.current += 1;
    const seq = permissionsSeqRef.current;
    const perms = await getEffectivePermissions();
    if (seq !== permissionsSeqRef.current) return;
    setEffectivePermissions(perms);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      roles,
      effectivePermissions,
      activeSiteId,
      sites,
      lastLogin,
      login,
      logout,
      setActiveSite,
      hasPermission,
      refreshPermissions,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      roles,
      effectivePermissions,
      activeSiteId,
      sites,
      lastLogin,
      login,
      logout,
      setActiveSite,
      hasPermission,
      refreshPermissions,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
